import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  partenaireInputSchema,
  partenaireSchema,
  type Partenaire,
  type PartenaireInput,
} from '../schemas/partenaire';

const COLLECTION = 'partenaires';

function db() {
  return getFirebaseClients().firestore;
}

function parse(raw: unknown, id: string): Partenaire {
  const data = raw as Record<string, unknown>;
  return partenaireSchema.parse({
    ...data,
    id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

function byOrder(a: Partenaire, b: Partenaire): number {
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  return a.name.localeCompare(b.name, 'fr');
}

/** Public: published partners only (trustless query — rule proves readability). */
export async function listPublishedPartenaires(): Promise<Partenaire[]> {
  const q = query(collection(db(), COLLECTION), where('published', '==', true));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION).sort(byOrder);
}

/** Admin: every partner. */
export async function listAllPartenaires(): Promise<Partenaire[]> {
  const snap = await getDocs(collection(db(), COLLECTION));
  return parseDocs(snap.docs, parse, COLLECTION).sort(byOrder);
}

export async function getPartenaire(id: string): Promise<Partenaire | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

export async function createPartenaire(input: PartenaireInput): Promise<string> {
  const parsed = partenaireInputSchema.parse(input);
  const now = new Date().toISOString();
  await setDoc(doc(db(), COLLECTION, parsed.slug), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return parsed.slug;
}

export async function updatePartenaire(
  id: string,
  patch: Partial<PartenaireInput>,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePartenaire(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
